import * as React from 'react';
import { browserHistory, RouteComponentProps, withRouter } from 'react-router';
import { IModalAction } from 'models/modal';
import { InjectedRouter } from 'react-router';
import { Location } from 'history';
import * as i18n from 'i18next';
import { connect, Dispatch } from 'react-redux';
import { IStore } from 'redux/IStore';
import { closeModal, showModal } from 'redux/modules/modal';
import { InjectedFormProps } from 'models/common';

// get the previous value of props or state as in componentDidUpdate
export function usePrevious(value) {
	// The ref object is a generic container whose current property is mutable ...
	// ... and can hold any value, similar to an instance property on a class
	const ref = React.useRef();

	// Store current value in ref
	React.useEffect(() => {
		ref.current = value;
	}, [value]); // Only re-run if value changes

	// Return previous value (happens before update in useEffect above)
	return ref.current;
}

const confirmDialogContent = (t: (s: string) => string) => (
	<>
		<h1>{t('BSPortal_Common_ConfirmationDialog_Sure')}</h1>
		<p>{t('Magazine_Article_ManageArticle_UnsavedChangesModal_Label_AboutUnsavedChanges')}</p>
		<p>{t('Magazine_Article_ManageArticle_UnsavedChangesModal_Label_LeaveWithoutSave')}</p>
	</>
);

function onRouterWillLeave(
	dirty: boolean,
	showModal: (modalType: string, modalProps: any) => IModalAction,
	closeModal: () => IModalAction,
	t: (key: string, object?: any) => string,
	url: string,
	route: any,
	router: InjectedRouter,
) {
	if (dirty) {
		showModal('ConfirmationDialog', {
			confirmAction: (confirm: boolean, url: string) => {
				closeModal();
				if (confirm) {
					router.setRouteLeaveHook(route, null);
					browserHistory.push(url);
				}
			},
			value: url,
			yesButtonText: t('BSPortal_Common_Yes'),
			noButtonText: t('BSPortal_Common_NoWait'),
			content: confirmDialogContent(t),
		});

		return false;
	}
}

export function confirmDiscardChanges(onDiscard: () => void) {
	return (dispatch: Dispatch<IStore>) => {
		dispatch(
			showModal('ConfirmationDialog', {
				confirmAction: (confirm: boolean) => {
					dispatch(closeModal());
					if (confirm) {
						onDiscard();
					}
				},
				yesButtonText: i18n.t('BSPortal_Common_Yes'),
				noButtonText: i18n.t('BSPortal_Common_NoWait'),
				content: confirmDialogContent(i18n.t),
			}),
		);
	};
}

export function preventRouterLeave<TProps extends InjectedFormProps<any>, T extends React.ComponentType<TProps> = any>(
	exceptForLocation?: (currentLocation: Location, nextLocation: Location) => boolean,
	exceptForProps?: (props: TProps) => boolean,
) {
	return (C: T): T => {
		const dispatchToProps = (dispatch: Dispatch<any>) => ({
			showModal: (type: string, modalProps: any) => dispatch(showModal(type, modalProps)),
			closeModal: () => dispatch(closeModal()),
		});
		class A extends React.Component<TProps & RouteComponentProps<any, any> & ReturnType<typeof dispatchToProps>> {
			constructor(props) {
				super(props);
				this.routerWillLeave = this.routerWillLeave.bind(this);
			}
			public componentDidMount() {
				if (!this.props.route) {
					console.error('You used the decorator on a component without a route prop');
					return;
				}
				this.props.router.setRouteLeaveHook(this.props.route, this.routerWillLeave);
			}
			public componentWillUnmount() {
				this.props.router.setRouteLeaveHook(this.props.route, null);
			}
			public routerWillLeave(nextLocation?: Location<string>) {
				if (typeof this.props.dirty === 'undefined') {
					console.error('You used the decorator on a component without a form');
					return true;
				}
				if (typeof exceptForProps === 'function' && exceptForProps(this.props)) {
					return true;
				}
				if (typeof exceptForLocation === 'function') {
					if (typeof this.props.router === 'undefined') {
						console.error('You used the decorator on a component without a router prop');
						return true;
					}
					if (exceptForLocation(this.props.location || (this.props.router as any).location, nextLocation)) {
						return true;
					}
				}
				return onRouterWillLeave(
					this.props.dirty,
					this.props.showModal,
					this.props.closeModal,
					i18n.t,
					nextLocation.pathname,
					this.props.route || this.route,
					this.props.router,
				);
			}
			public render() {
				const B = C as any;
				return <B {...this.props} />;
			}
			private get route(): RouteComponentProps<{}, {}>['route'] {
				if ('route' in this.props) {
					return (this.props as any).route;
				} else {
					const routes = (this.props as any).routes;
					if (routes.length) {
						return routes[routes.length - 1];
					}
				}
			}
		}
		return withRouter(connect(null, dispatchToProps)(A as any) as any) as T;
	};
}

export interface IDropdownProps {
	open?: boolean;
	toggle?: (state?: boolean | React.MouseEvent<any>) => void;
	ref1?: React.RefObject<any>;
	ref2?: React.RefObject<any>;
}

type CloseFnTuple = [symbol, () => void];

const closeFunctions: CloseFnTuple[] = [];
const classNamesToIgnore: Set<string> = new Set(['mobileLanguageSelector', 'mobileHeader__mobileLanguages__title']); // put element classNames that should not cause the dropdown to close here

export function closeAllDropdowns() {
	closeFunctions.forEach((t) => t[1]());
}

export function dropdown<T = any>() {
	return (C: T): T => {
		class A extends React.Component<any, { open: boolean }> {
			constructor(props) {
				super(props);

				this.toggle = this.toggle.bind(this);
				this.close = this.close.bind(this);

				this.state = {
					open: false,
				};
			}
			private ref1 = React.createRef<any>();
			private ref2 = React.createRef<any>();
			uniq = Symbol();
			public componentDidMount(): void {
				document.addEventListener('click', this.close);
				closeFunctions.push([this.uniq, this.close]);
			}

			public componentWillUnmount() {
				document.removeEventListener('click', this.close);
				const index = closeFunctions.findIndex((t) => t[0] === this.uniq);
				if (index >= 0) {
					closeFunctions.splice(index, 1);
				}
			}
			public componentDidUpdate(prevProps) {
				if (this.props.location !== prevProps.location) {
					this.close();
				}
			}
			private close(e?: MouseEvent) {
				const refs = [this.ref1?.current, this.ref2?.current].filter(Boolean);
				if (e?.target && refs.length) {
					const nodeTarget = e.target as Node;
					for (const ref of refs) {
						if (ref && (ref.contains(nodeTarget) || ref === nodeTarget)) {
							return;
						}
					}
				}
				if (this.state.open) {
					const elementClassName = (e as any)?.target?.className;

					// If no target or not on ignore list then close
					if (!elementClassName || !classNamesToIgnore?.has(elementClassName)) {
						this.setState({ open: false });
					}
				}
			}
			private toggle(open?: boolean | React.MouseEvent<any>) {
				if (typeof open === 'boolean') {
					if (this.state.open !== open) {
						this.setState({ open });
					}
				} else {
					this.setState((s) => ({ open: !s.open }));
				}
			}

			public render() {
				const B = C as any;
				return <B ref1={this.ref1} ref2={this.ref2} {...this.props} open={this.state.open} toggle={this.toggle} />;
			}
		}
		return withRouter(A as any) as T;
	};
}
